/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Certification } from '../../shared/models';
import { AcademyNavigationService } from '../../services/academy-navigation.service';

@Component({
  selector: 'ut-certification-card',
  templateUrl: './certification-card.component.html',
  styleUrls: ['./certification-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class CertificationCardComponent {
  private static nextId = 0;

  @Input() certification!: Certification;
  readonly clipId = ++CertificationCardComponent.nextId;

  constructor(private navService: AcademyNavigationService) {}

  get statusLabel(): string {
    switch (this.certification.status) {
      case 'in-progress': return 'In-Progress';
      case 'in-redo': return 'In-Redo';
      case 'available': return 'Available';
      case 'locked': return 'Locked';
      case 'completed': return 'Completed';
      default: return '';
    }
  }

  get showRunInfo(): boolean {
    const { status, max_allowed_runs: maxAllowedRuns } = this.certification;
    return maxAllowedRuns > 1 && (status === 'completed' || status === 'in-redo');
  }

  get showAllowedRuns(): boolean {
    const { status, max_allowed_runs: maxAllowedRuns } = this.certification;
    return maxAllowedRuns > 1 && (status === 'locked' || status === 'available' || status === 'in-progress');
  }

  get showCompletionDate(): boolean {
    const { status, max_allowed_runs: maxAllowedRuns } = this.certification;
    return status === 'completed' && maxAllowedRuns <= 1 && !!this.certification.completion_date;
  }

  get inviteOnlyLabel(): string | null {
    return this.certification.availability === 'invite_only' ? '(Invite Only)' : null;
  }

  get isLocked(): boolean {
    return this.certification.status === 'locked';
  }

  get showDetailsButton(): boolean {
    return ['available', 'in-progress', 'in-redo'].includes(this.certification.status);
  }

  onSeeDetails(): void {
    this.navService.goToCertificationDetail(this.certification.id);
  }
}
